import { Quote, QuoteStatus } from '../../../mocks/quotes';

interface QuoteCardProps {
  quote: Quote;
  onPreview: (quote: Quote) => void;
  onSend: (quote: Quote) => void;
  onConvert: (quote: Quote) => void;
  onDuplicate: (quote: Quote) => void;
}

const statusConfig: Record<QuoteStatus, { bg: string; text: string; dot: string; label: string }> = {
  Draft: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Draft' },
  Sent: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Sent' },
  Accepted: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Accepted' },
  Rejected: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400', label: 'Rejected' },
  Expired: { bg: 'bg-orange-50', text: 'text-orange-600', dot: 'bg-orange-400', label: 'Expired' },
};

const categoryColors: Record<string, string> = {
  Accommodation: 'bg-indigo-50 text-indigo-600',
  Flight: 'bg-sky-50 text-sky-600',
  Cruise: 'bg-teal-50 text-teal-600',
  Activity: 'bg-violet-50 text-violet-600',
  Transfer: 'bg-orange-50 text-orange-600',
  Insurance: 'bg-rose-50 text-rose-600',
  'Special Arrangement': 'bg-pink-50 text-pink-600',
  Transport: 'bg-cyan-50 text-cyan-600',
  Service: 'bg-yellow-50 text-yellow-700',
  Dining: 'bg-lime-50 text-lime-600',
};

function formatCurrency(amount: number) {
  return `AED ${amount.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function QuoteCard({ quote, onPreview, onSend, onConvert, onDuplicate }: QuoteCardProps) {
  const status = statusConfig[quote.status];
  const categories = [...new Set(quote.items.map((i) => i.category))].slice(0, 3);

  const isExpired = new Date(quote.validUntil) < new Date() && quote.status === 'Sent';
  const daysUntilExpiry = Math.ceil((new Date(quote.validUntil).getTime() - Date.now()) / 86400000);

  return (
    <div className="bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono font-semibold text-gray-400">{quote.id}</span>
            {quote.version > 1 && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">
                v{quote.version}
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{quote.title}</h3>
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </div>

      {/* Client Info */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
          {quote.leadName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{quote.leadName}</p>
          <p className="text-xs text-gray-400 truncate">{quote.leadEmail}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-bold text-gray-800">{formatCurrency(quote.totalAmount)}</p>
          <p className="text-xs text-gray-400">{quote.pax} pax</p>
        </div>
      </div>

      {/* Service Tags */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map((cat) => (
          <span key={cat} className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[cat] || 'bg-gray-100 text-gray-500'}`}>
            {cat}
          </span>
        ))}
        {quote.items.length > 3 && (
          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
            +{quote.items.length - 3} more
          </span>
        )}
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-0.5">Destination</p>
          <p className="font-semibold text-gray-700 truncate">{quote.destination}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-0.5">Travel Date</p>
          <p className="font-semibold text-gray-700">
            {new Date(quote.travelDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-0.5">Valid Until</p>
          <p className={`font-semibold truncate ${isExpired || daysUntilExpiry <= 3 ? 'text-red-500' : 'text-gray-700'}`}>
            {new Date(quote.validUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            {daysUntilExpiry > 0 && daysUntilExpiry <= 5 && quote.status === 'Sent' && (
              <span className="text-orange-500"> ({daysUntilExpiry}d left)</span>
            )}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-0.5">Agent</p>
          <p className="font-semibold text-gray-700 truncate">{quote.agent.split(' ')[0]}</p>
        </div>
      </div>

      {/* Pricing summary */}
      {(quote.discountAmount > 0 || quote.taxAmount > 0) && (
        <div className="border-t border-gray-50 pt-3 text-xs space-y-1">
          {quote.discountAmount > 0 && (
            <div className="flex justify-between text-gray-500">
              <span>Discount {quote.discountType === 'percent' ? `(${quote.discountValue}%)` : ''}</span>
              <span className="text-emerald-600 font-medium">-{formatCurrency(quote.discountAmount)}</span>
            </div>
          )}
          {quote.taxAmount > 0 && (
            <div className="flex justify-between text-gray-500">
              <span>VAT ({quote.taxRate}%)</span>
              <span>+{formatCurrency(quote.taxAmount)}</span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => onPreview(quote)}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-eye-line text-sm" /> Preview
        </button>
        {quote.status === 'Draft' && (
          <button
            onClick={() => onSend(quote)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line text-sm" /> Send
          </button>
        )}
        {quote.status === 'Sent' && (
          <button
            onClick={() => onSend(quote)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line text-sm" /> Resend
          </button>
        )}
        {quote.status === 'Accepted' && (
          <button
            onClick={() => onConvert(quote)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-checkbox-circle-line text-sm" /> Convert
          </button>
        )}
        {(quote.status === 'Rejected' || quote.status === 'Expired') && (
          <button
            onClick={() => onDuplicate(quote)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line text-sm" /> Revise
          </button>
        )}
        <button
          onClick={() => onDuplicate(quote)}
          className="w-9 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 transition-colors cursor-pointer flex-shrink-0"
          title="Duplicate"
        >
          <i className="ri-file-copy-2-line text-sm" />
        </button>
      </div>
    </div>
  );
}
